import React from 'react';
import { useQuery } from 'react-admin';
import { Box, Typography, CircularProgress } from '@material-ui/core';
import * as R from 'ramda';
import moment from 'moment-timezone';

// Daily attendance rate (per session): minutes attended for this session / total minutes of this session
// Supports overrides to use prefetched data and avoid extra network calls
const ZoomAttendanceRateFieldDaily = ({
  record,
  date,
  session,
  timezone,
  attendanceDataOverride,
  sessionsDataOverride,
  loadingOverride,
  sessionByUuid,
  totalMinutesOverride,
}) => {
  // Fetch attendance entries for the student on this specific date
  const attendanceQuery = useQuery({
    type: 'getManyReference',
    resource: 'zoom_attendance',
    payload: {
      search: [
        {
          $match: {
            $and: [
              { student_id: record.id },
              { cohort_id: record.cohort },
              { session_date: moment(date).format('YYYY-MM-DD') },
            ],
          },
        },
      ],
      pagination: {
        perPage: 1000,
      },
    },
  });

  const attendanceData = attendanceDataOverride ?? attendanceQuery.data;
  const attendanceLoading = loadingOverride ?? attendanceQuery.loading;
  const attendanceError = attendanceDataOverride ? null : attendanceQuery.error;

  // Daily rate: use all attendance of that day for the student (no session split)
  const filteredAttendance = React.useMemo(
    () => attendanceData || [],
    [attendanceData]
  );

  // Get session UUIDs from filtered attendance data
  const sessionUuids = React.useMemo(() => {
    if (!filteredAttendance || R.isEmpty(filteredAttendance)) return [];
    return R.pipe(
      R.pluck('zoom_meeting_uuid'),
      R.reject(R.isNil),
      R.uniq
    )(filteredAttendance);
  }, [filteredAttendance]);

  // Fetch sessions for this date and cohort
  const sessionQuery = useQuery({
    type: 'getManyReference',
    resource: 'zoom_sessions',
    payload: {
      search: [
        {
          $match: {
            $and: [
              { cohort_id: record.cohort },
              { uuid: { $in: sessionUuids } },
            ],
          },
        },
      ],
      pagination: {
        perPage: 1000,
      },
    },
  });

  const sessionData = sessionsDataOverride ?? sessionQuery.data;
  const sessionLoading = loadingOverride ?? sessionQuery.loading;
  const sessionError = sessionsDataOverride ? null : sessionQuery.error;

  // Build a session map from available sessions (override or fetched)
  const sessionMap = React.useMemo(() => {
    if (sessionByUuid) return sessionByUuid;
    const list = sessionData || [];
    return list.reduce((acc, s) => {
      if (s && s.uuid) acc[s.uuid] = s;
      return acc;
    }, {});
  }, [sessionByUuid, sessionData]);

  if (attendanceLoading || sessionLoading) {
    return (
      <Box display="flex" alignItems="center">
        <CircularProgress size={20} />
      </Box>
    );
  }

  if (
    (attendanceError && attendanceError.message !== 'No attendance data') ||
    sessionError
  ) {
    return (
      <Typography
        variant="body2"
        color="error"
        sx={{
          textAlign: 'center',
        }}
      >
        Error
      </Typography>
    );
  }

  if (
    !filteredAttendance ||
    R.isEmpty(filteredAttendance) ||
    !sessionData ||
    R.isEmpty(sessionData)
  ) {
    return (
      <Typography
        variant="body2"
        color="textSecondary"
        sx={{
          textAlign: 'center',
        }}
      >
        0%
      </Typography>
    );
  }

  // Calculate total minutes attended grouped by uuid using backend duration
  const minutesByUuid = R.reduce(
    (acc, a) => {
      const uuid = a.zoom_meeting_uuid;
      if (!uuid) return acc;
      const inc = Math.max(0, a.duration || 0);
      acc[uuid] = (acc[uuid] || 0) + inc;
      return acc;
    },
    {},
    filteredAttendance || []
  );

  const totalAttendedMinutes =
    totalMinutesOverride ?? R.pipe(Object.values, R.sum)(minutesByUuid);

  // Compute denominator using only the uuids present in the student's attendance for that day
  const totalClassMinutes = (sessionUuids || []).reduce((sum, u) => {
    const duration = sessionMap?.[u]?.duration || 0;
    return sum + duration;
  }, 0);

  // Avoid division by zero
  if (!totalClassMinutes) {
    return (
      <Typography
        variant="body2"
        color="textSecondary"
        sx={{ textAlign: 'center' }}
      >
        0%
      </Typography>
    );
  }

  const percentage = Math.min(
    100,
    Math.round((totalAttendedMinutes / totalClassMinutes) * 100)
  );

  return (
    <Box>
      <Typography
        variant="body2"
        color="textPrimary"
        sx={{
          textAlign: 'center',
          fontWeight: 'medium',
        }}
      >
        {`${percentage}%`}
      </Typography>
    </Box>
  );
};

export default ZoomAttendanceRateFieldDaily;
